import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useLocation, useNavigate } from "react-router-dom";

import { usePermissions } from "hooks/permission";
import { useMediaPermissions } from "hooks/useMediaPermissions";

import {
  getDevicesThunk,
  restorePreferencesThunk,
} from "store/modules/mediaDevices/actions/thunks";
import { setScreenStatus } from "store/modules/screenControl/actions";
import { useAppDispatch, useAppSelector } from "store/rootReducer";

import LoadingComponent from "components/LoadingComponent";
import ModalDefault from "components/ModalDefault";
import SearchEmpty from "components/SearchEmpty";

import api from "services/api";

import CameraIcon from "assets/svg/CameraIcon.svg?react";
import CheckIcon from "assets/svg/v.svg?react";

import { FORMS_HEARING } from "utils/entities/permission";

import * as Styled from "./styles";

const HearingRecordLocal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hearingId = searchParams.get("hearingId") ?? "";
  const hearingFileId = searchParams.get("hearingFileId") ?? "";
  const dispatch = useAppDispatch();

  const { selectedVideo, selectedMic } = useAppSelector(
    (state) => state.mediaDevices,
  );

  const { create } = usePermissions(FORMS_HEARING.HEARING);
  const mediaPermissions = useMediaPermissions();

  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [showConnectionBadge, setShowConnectionBadge] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null,
  );
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeProgress, setFinalizeProgress] = useState(0);

  const [showFinalizeErrorModal, setShowFinalizeErrorModal] = useState(false);

  const finalizeRecordingRequest = useCallback(async () => {
    try {
      await api.post("/v1/HearingFile/finalize-recording", {
        hearingId,
        hearingFileId,
      });
      setFinalizeProgress(100);
      return true;
    } catch (error) {
      console.error("Erro ao finalizar a gravação", error);
      return false;
    }
  }, [hearingId, hearingFileId]);

  const retryFinalizeRecording = async () => {
    setShowFinalizeErrorModal(false);
    setIsFinalizing(true);
    setFinalizeProgress(0);

    const success = await finalizeRecordingRequest();

    setIsFinalizing(false);

    if (success) {
      navigate(
        `/audiences?hearingId=${hearingId}&hearingFileId=${hearingFileId}`,
        { replace: true },
      );
    } else {
      setShowFinalizeErrorModal(true);
    }
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunkIndexRef = useRef<number>(0);
  const uploadQueueRef = useRef<Array<{ blob: Blob; index: number }>>([]);
  const isProcessingQueueRef = useRef<boolean>(false);

  const dbPromise = useRef<Promise<IDBDatabase> | null>(null);

  const getDb = useCallback(() => {
    if (!dbPromise.current) {
      dbPromise.current = new Promise((resolve, reject) => {
        const request = indexedDB.open("hearingChunks", 1);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains("chunks")) {
            db.createObjectStore("chunks", { keyPath: "index" });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
    return dbPromise.current;
  }, []);

  const saveChunkToDB = useCallback(
    async (index: number, blob: Blob) => {
      try {
        const db = await getDb();
        const tx = db.transaction("chunks", "readwrite");
        tx.objectStore("chunks").put({ index, blob });
      } catch (e) {
        console.error("Erro ao salvar chunk no IndexedDB", e);
      }
    },
    [getDb],
  );

  const deleteChunkFromDB = useCallback(
    async (index: number) => {
      try {
        const db = await getDb();
        const tx = db.transaction("chunks", "readwrite");
        tx.objectStore("chunks").delete(index);
      } catch (e) {
        console.error("Erro ao deletar chunk do IndexedDB", e);
      }
    },
    [getDb],
  );

  const dbRequestToPromise = <T,>(request: IDBRequest<T>): Promise<T> =>
    new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

  const getAllChunksFromDB = useCallback(async (): Promise<
    Array<{ index: number; blob: Blob }>
  > => {
    try {
      const db = await getDb();
      const tx = db.transaction("chunks", "readonly");
      const request = tx.objectStore("chunks").getAll();
      const result: Array<{ index: number; blob: Blob }> =
        await dbRequestToPromise(request);
      return result?.sort((a, b) => a.index - b.index) ?? [];
    } catch (e) {
      console.error("Erro ao buscar chunks do IndexedDB", e);
      return [];
    }
  }, [getDb]);

  const updateConnectionBadgeStatus = useCallback(async () => {
    const pendingChunks = await getAllChunksFromDB();
    setShowConnectionBadge(pendingChunks.length > 0);
  }, [getAllChunksFromDB]);

  const sendChunkToBackend = useCallback(
    async (blob: Blob, index: number) => {
      const formData = new FormData();
      formData.append("index", index.toString());
      formData.append("chunk", blob, `chunk-${index}.webm`);

      formData.append("hearingId", hearingId);
      formData.append("hearingFileId", hearingFileId);

      await api.post("/v1/HearingFile/upload-chunk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    [hearingId, hearingFileId],
  );

  const processUploadQueue = useCallback(async () => {
    if (isProcessingQueueRef.current || uploadQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    const chunk = uploadQueueRef.current[0];

    try {
      await sendChunkToBackend(chunk.blob, chunk.index);
      await deleteChunkFromDB(chunk.index);
      uploadQueueRef.current.shift();
      updateConnectionBadgeStatus();

      if (uploadQueueRef.current.length > 0) {
        setTimeout(() => {
          isProcessingQueueRef.current = false;
          processUploadQueue();
        }, 100);
      } else {
        isProcessingQueueRef.current = false;
      }
    } catch {
      await saveChunkToDB(chunk.index, chunk.blob);
      setShowConnectionBadge(true);

      uploadQueueRef.current.shift();
      isProcessingQueueRef.current = false;

      const retryDelay = 5000;
      setTimeout(() => {
        uploadQueueRef.current.push(chunk);
        processUploadQueue();
      }, retryDelay);
    }
  }, [
    sendChunkToBackend,
    deleteChunkFromDB,
    saveChunkToDB,
    updateConnectionBadgeStatus,
  ]);

  const enqueueChunkWithRetry = useCallback(
    (blob: Blob, index: number) => {
      uploadQueueRef.current.push({ blob, index });
      processUploadQueue();
    },
    [processUploadQueue],
  );

  const processPendingChunks = useCallback(async () => {
    const pendingChunks = await getAllChunksFromDB();

    if (pendingChunks.length > 0) {
      pendingChunks.forEach(({ blob, index }) => {
        const isInQueue = uploadQueueRef.current.some(
          (item) => item.index === index,
        );
        if (!isInQueue) {
          uploadQueueRef.current.push({ blob, index });
        }
      });

      setShowConnectionBadge(true);
      if (!isProcessingQueueRef.current) {
        processUploadQueue();
      }
    } else {
      setShowConnectionBadge(false);
    }
  }, [getAllChunksFromDB, processUploadQueue]);

  useEffect(() => {
    processPendingChunks();

    const handleOnline = () => {
      processPendingChunks();
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [processPendingChunks]);

  const formatElapsedTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, "0");
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const seconds = Math.floor(totalSeconds % 60)
      .toString()
      .padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  };

  const handleStartRecording = () => {
    if (!streamRef.current) return;

    try {
      const recorder = new MediaRecorder(streamRef.current, {
        mimeType: "video/webm;codecs=vp8,opus",
      });

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          const currentIndex = chunkIndexRef.current;
          chunkIndexRef.current += 1;
          enqueueChunkWithRetry(event.data, currentIndex);
        }
      };

      recorder.onstop = async () => {
        try {
          const totalChunksRecorded = chunkIndexRef.current;
          const MAX_FLUSH_RETRIES = 120;

          await new Promise((res) => setTimeout(res, 1000));
          await processPendingChunks();

          for (let i = 0; i < MAX_FLUSH_RETRIES; i++) {
            const pending = await getAllChunksFromDB();
            const sent = totalChunksRecorded - pending.length;
            const progress = Math.min(
              totalChunksRecorded
                ? Math.floor((sent / totalChunksRecorded) * 100)
                : 0,
              90,
            );
            setFinalizeProgress(progress);

            if (pending.length === 0) break;
            await new Promise((res) => setTimeout(res, 1000));
          }

          const success = await finalizeRecordingRequest();

          if (success) {
            navigate(
              `/audiences?hearingId=${hearingId}&hearingFileId=${hearingFileId}`,
              { replace: true },
            );
          } else {
            setShowFinalizeErrorModal(true);
          }
        } catch (error) {
          console.error("Erro ao finalizar a gravação", error);
          setShowFinalizeErrorModal(true);
        } finally {
          setIsRecordingStarted(false);
          setIsFinalizing(false);
        }
      };

      const backendStartTime = location.state?.recordStartTime
        ? new Date(location.state.recordStartTime).getTime()
        : Date.now();

      setRecordingStartTime(backendStartTime);

      chunkIndexRef.current = 0;

      recorder.start(5000);
      setIsRecordingStarted(true);
    } catch (error) {
      console.error("Erro ao iniciar gravação", error);
    }
  };

  const handleFinishRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    setShowFinishModal(false);

    setIsFinalizing(true);
    setFinalizeProgress(0);
  };

  useEffect(() => {
    if (!location.state) {
      navigate(
        `/audiences?hearingId=${hearingId}&hearingFileId=${hearingFileId}`,
      );
      return;
    }

    dispatch(setScreenStatus(true));
  }, [dispatch, location.state, navigate, hearingId, hearingFileId]);

  useEffect(() => {
    dispatch(getDevicesThunk());
    dispatch(restorePreferencesThunk());
  }, [dispatch]);

  useEffect(() => {
    let currentStream: MediaStream | null = null;

    const startCamera = async () => {
      if (
        !mediaPermissions.camera ||
        !mediaPermissions.mic ||
        !videoRef.current
      )
        return;

      try {
        const baseVideoConstraint = {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        } as const;

        const constraints: MediaStreamConstraints = {
          video: selectedVideo?.id
            ? { ...baseVideoConstraint, deviceId: { exact: selectedVideo.id } }
            : baseVideoConstraint,
          audio: selectedMic?.id
            ? { deviceId: { exact: selectedMic.id } }
            : true,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        currentStream = stream;
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
      } catch (error) {
        console.error("Erro ao iniciar câmera", error);
      }
    };

    startCamera();

    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = null;
    };
  }, [
    mediaPermissions.camera,
    mediaPermissions.mic,
    selectedVideo,
    selectedMic,
  ]);

  useEffect(() => {
    if (!isRecordingStarted || !recordingStartTime) return;

    const intervalId = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - recordingStartTime) / 1000));
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isRecordingStarted, recordingStartTime]);

  return (
    <Styled.Container>
      <Styled.HeaderContainer>
        <Styled.Title>{t("audiences.singular")} {location.state?.procedureNumber}</Styled.Title>
      </Styled.HeaderContainer>
      {create ? (
        <Styled.BodyContainer>
          {mediaPermissions.camera && mediaPermissions.mic ? (
            <>
              <Styled.PreviewContainer>
                {isRecordingStarted && !isFinalizing && (
                  <Styled.RecordingBadge>
                    <Styled.RecordingDot />
                    Gravando - {formatElapsedTime(elapsedTime)}
                  </Styled.RecordingBadge>
                )}

                {showConnectionBadge && (
                  <Styled.ConnectionBadge>
                    <Styled.ConnectionDot />
                    Conexão instável
                  </Styled.ConnectionBadge>
                )}

                <Styled.PreviewVideo
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                />
              </Styled.PreviewContainer>
              <Styled.FooterContainer>
                <Styled.RecordPauseButton
                  onClick={handleStartRecording}
                  disabled={
                    !mediaPermissions.camera ||
                    !mediaPermissions.mic ||
                    isRecordingStarted
                  }
                >
                  <CameraIcon />
                </Styled.RecordPauseButton>

                <Styled.FinishButton
                  disabled={!isRecordingStarted}
                  onClick={() => setShowFinishModal(true)}
                >
                  <CheckIcon />
                  Encerrar gravação
                </Styled.FinishButton>
              </Styled.FooterContainer>
            </>
          ) : (
            <Styled.DeniedContainer>
              <SearchEmpty
                typeIcon="video"
                textMessage={t("common.noPermissionForProfile")}
              />
            </Styled.DeniedContainer>
          )}
        </Styled.BodyContainer>
      ) : (
        <Styled.DeniedContainer>
          <SearchEmpty
            typeIcon="avatar"
            textMessage={t("common.noPermissionForProfile")}
          />
        </Styled.DeniedContainer>
      )}

      <ModalDefault
        openModal={showFinishModal}
        setOpenModal={setShowFinishModal}
        title={t("recording.finishRecording")}
        bodyMessageTitle={t("recording.confirmFinish")}
        bodyMessageSubTitle={t("common.actionCannotBeUndone")}
        textConfirm={t("common.finish").toUpperCase()}
        onClickConfirm={handleFinishRecording}
        textCancel={t("common.cancel").toUpperCase()}
        bodyMessageMarginBetweenTitleAndSubTitle="30px"
        bodyMessageMarginTop="30px"
        onClickCancel={() => {
          setShowFinishModal(false);
        }}
      />

      <LoadingComponent
        loading={isFinalizing}
        label={`${t("common.loading")}... ${finalizeProgress}%`}
      />

      <ModalDefault
        openModal={showFinalizeErrorModal}
        setOpenModal={setShowFinalizeErrorModal}
        title={t("recording.finalizationError")}
        bodyMessageTitle={t("recording.finalizationErrorMessage")}
        bodyMessageSubTitle={""}
        textConfirm={t("common.tryAgain").toUpperCase()}
        onClickConfirm={retryFinalizeRecording}
        textCancel={t("common.cancel").toUpperCase()}
        bodyMessageMarginTop="30px"
        bodyMessageMarginBetweenTitleAndSubTitle="20px"
        onClickCancel={() => setShowFinalizeErrorModal(false)}
      />
    </Styled.Container>
  );
};

export default HearingRecordLocal;
